import { useState, useRef, useEffect } from "react";
import { Modal } from "react-bootstrap";
import "./UserGuideModal.css";

/* =============================================
   CHAPTERS — Complete User-Friendly Guide
   ============================================= */
const CHAPTERS = [
  /* ─── Chapter 1: Welcome ─── */
  {
    id: "welcome",
    title: "Welcome to Zoomey",
    icon: "bi-book",
    intro:
      "Zoomey is your personal assistant that lives right in your browser. It can manage your tasks, take notes, set reminders, search the internet, read your emails, manage your calendar, handle grocery lists, store contacts, and so much more. You just talk to it in plain English (or Hinglish!) and it does the work for you. No complicated menus or buttons to learn \u2014 just type what you need like you're texting a friend.",
    content: (
      <>
        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-chat-dots"></i>
            </div>
            <div className="guide-feature-block-title">How to Talk to Zoomey</div>
          </div>
          <p className="guide-feature-block-desc">
            At the bottom of the screen, you'll see a text box \u2014 that's where you type
            your messages. Just write whatever you need and press <strong>Enter</strong> (or
            click the send button). There are no special commands, codes, or syntax to
            remember. Just talk naturally, the way you'd message someone on WhatsApp.
          </p>
          <p className="guide-feature-block-desc">
            Zoomey understands regular sentences, questions, instructions, and even casual
            language. You'll see its reply appear in real-time, word by word, like a live
            chat. If Zoomey's reply is long, you can scroll through it. Each conversation
            message appears as a bubble \u2014 your messages on one side and Zoomey's
            responses on the other.
          </p>
          <div className="guide-try-label">Try typing these to get started</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"What can you do?"</span>
            <span className="guide-try-chip">"Add a task: buy groceries tomorrow"</span>
            <span className="guide-try-chip">"What's the latest news?"</span>
            <span className="guide-try-chip">"Remind me to call mom at 6pm"</span>
          </div>
          <div className="guide-tip">
            <strong>Tip:</strong> You can ask multiple things in one message! For example:
            "Add a task to buy milk and also remind me about it tomorrow at 10am."
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-at"></i>
            </div>
            <div className="guide-feature-block-title">Mentioning Things with @</div>
          </div>
          <p className="guide-feature-block-desc">
            When you type the <strong>@</strong> symbol in the chat box, a dropdown menu will
            pop up showing your existing items \u2014 tasks, notes, lists, and conversation
            sessions. Click on any item to "mention" it. This tells Zoomey exactly which
            item you're referring to, so it can update, delete, or reference the right thing
            without confusion.
          </p>
          <p className="guide-feature-block-desc">
            Think of it like tagging someone in a WhatsApp group \u2014 but here you're tagging
            your tasks and notes instead of people. This is especially useful when you have
            many items with similar names, because the @ mention ensures Zoomey picks the
            right one.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Mark @Buy groceries as done"</span>
            <span className="guide-try-chip">"What's left in @Shopping List?"</span>
            <span className="guide-try-chip">"Add milk to @Grocery List"</span>
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-collection"></i>
            </div>
            <div className="guide-feature-block-title">Topics (Organizing Your Chats)</div>
          </div>
          <p className="guide-feature-block-desc">
            By default, all your conversations happen in one place called <strong>"Main
            Topic"</strong>. This is your primary chat with Zoomey where everything
            happens. For most people, this is all they need.
          </p>
          <p className="guide-feature-block-desc">
            But if you want to organize conversations by subject, you can create
            <strong> Topics</strong>. Think of topics like separate folders or tabs for
            different conversations. For example, you could have a "Work" topic for all
            work-related chats and a "Personal" topic for personal things. Each topic keeps
            its own separate chat history, so nothing gets mixed up. You can even create
            <strong> Subtopics</strong> inside topics for deeper organization (like folders
            inside folders).
          </p>
          <ol className="guide-steps">
            <li>Click on any topic in the <strong>left sidebar</strong> to switch to it</li>
            <li>Each topic has its own completely separate chat history</li>
            <li>To go back to your main chat, click your <strong>name (dropdown)</strong> in the top-right and select <strong>"Main Topic"</strong></li>
          </ol>
          <div className="guide-tip">
            <strong>Tip:</strong> You don't have to use topics at all! The Main Topic works
            perfectly fine for everything. Topics are just there if you like things organized.
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-mic"></i>
            </div>
            <div className="guide-feature-block-title">Voice Chat (Talk Instead of Type)</div>
          </div>
          <p className="guide-feature-block-desc">
            Don't want to type? No problem! Click the <strong>microphone icon</strong> next to
            the text input box and start speaking. Zoomey will convert your speech to text
            automatically and then respond. You can also have a real-time voice conversation
            \u2014 Zoomey will speak back to you, just like talking on a phone call!
          </p>
          <p className="guide-feature-block-desc">
            This is perfect when you're cooking, driving, working out, or just feeling lazy.
            Everything you can do by typing, you can also do by speaking. Say "add a task
            to call the dentist" and it works just the same as typing it.
          </p>
          <div className="guide-tip">
            <strong>Tip:</strong> Your browser will ask for microphone permission the first time
            you click the mic icon. Make sure to click "Allow" so the voice feature can work.
          </div>
        </div>
      </>
    ),
  },

  /* ─── Chapter 2: Simple vs Smart Mode ─── */
  {
    id: "modes",
    title: "Simple & Smart Mode",
    icon: "bi-toggles2",
    intro:
      "Zoomey has two modes you can switch between at any time. Think of it like a car \u2014 Simple Mode is your daily driving mode (fast, efficient, handles most things), and Smart Mode is the turbo mode (more powerful, can do more things, but uses more energy). You can switch back and forth whenever you want.",
    content: (
      <>
        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon" style={{ background: "rgba(250, 84, 0, 0.1)", color: "var(--orange-bg)" }}>
              <i className="bi bi-lightning-charge"></i>
            </div>
            <div className="guide-feature-block-title">Simple Mode (Default \u2014 What You Start With)</div>
          </div>
          <p className="guide-feature-block-desc">
            When you first open Zoomey, you're in Simple Mode. This mode uses a
            <strong> lighter, faster brain</strong> that responds almost instantly. It's
            designed for your everyday needs and handles the most common tasks effortlessly.
            Simple Mode is perfect for:
          </p>
          <ul className="guide-steps">
            <li>Adding, viewing, updating, and deleting <strong>tasks</strong></li>
            <li>Creating and reading <strong>notes</strong></li>
            <li>Setting <strong>reminders</strong> ("remind me at 8am...")</li>
            <li>Managing <strong>grocery lists</strong></li>
            <li>Having general <strong>conversations</strong> and asking questions</li>
            <li><strong>Searching the web</strong> for latest information</li>
            <li>Managing <strong>contacts</strong> (save phone numbers, emails)</li>
            <li>Viewing and managing <strong>notifications</strong></li>
            <li>Using the <strong>Scratchpad</strong> quick notepad</li>
            <li>Managing your <strong>custom lists</strong></li>
          </ul>
          <div className="guide-tip">
            <strong>Tip:</strong> For most daily activities, Simple Mode is all you need. It's
            faster and saves processing power. Only switch to Smart Mode when you need the
            extra features listed below.
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon" style={{ background: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6" }}>
              <i className="bi bi-gem"></i>
            </div>
            <div className="guide-feature-block-title">Smart Mode (Full Power)</div>
          </div>
          <p className="guide-feature-block-desc">
            When you need the full power of Zoomey, switch to Smart Mode. It uses a
            <strong> more intelligent brain</strong> that can handle complex requests and
            has access to <strong>all 32+ tools</strong>. Smart Mode includes everything
            in Simple Mode PLUS these extra capabilities:
          </p>
          <ul className="guide-steps">
            <li><strong>Email (Gmail)</strong> \u2014 Read, send, search, and reply to emails</li>
            <li><strong>Google Calendar</strong> \u2014 View schedule, create events, check availability</li>
            <li><strong>Google Docs, Drive & Sheets</strong> \u2014 Access and work with your Google files</li>
            <li><strong>Deep Research</strong> \u2014 Detailed multi-source research reports</li>
            <li><strong>File & Image Analysis</strong> \u2014 Upload and analyze PDFs, images, documents</li>
            <li><strong>Second Brain</strong> \u2014 Your personal knowledge storage system</li>
            <li><strong>Scheduled Tasks</strong> \u2014 Set up automated recurring actions</li>
            <li><strong>Meeting Notes</strong> \u2014 Access meeting transcriptions</li>
          </ul>
          <p className="guide-feature-block-desc">
            Smart Mode takes a little longer to respond because it's thinking more deeply,
            but the answers are more detailed and thoughtful. It also remembers more of your
            conversation (up to 16 recent messages vs. 10 in Simple Mode).
          </p>
          <div className="guide-note">
            <strong>Note:</strong> If you ask for something that needs Smart Mode while
            you're in Simple Mode, Zoomey will politely let you know and suggest switching \u2014
            so you'll never miss out on a feature!
          </div>
        </div>

        <table className="guide-mode-table">
          <thead>
            <tr>
              <th>What you get</th>
              <th><span className="mode-dot orange"></span>Simple Mode</th>
              <th><span className="mode-dot purple"></span>Smart Mode</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Speed</td><td>Very fast replies</td><td>Slightly slower, more detailed</td></tr>
            <tr><td>Basic features (tasks, notes, reminders)</td><td>{"\u2714"} Yes</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>Web search</td><td>{"\u2714"} Yes</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>Memory & contacts</td><td>{"\u2714"} Yes</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>Lists & groceries</td><td>{"\u2714"} Yes</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>Email (Gmail)</td><td>{"\u2718"} No</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>Google Calendar</td><td>{"\u2718"} No</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>Google Docs / Drive / Sheets</td><td>{"\u2718"} No</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>Deep Research (detailed analysis)</td><td>{"\u2718"} No</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>File & Image Analysis</td><td>{"\u2718"} No</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>Second Brain (knowledge storage)</td><td>{"\u2718"} No</td><td>{"\u2714"} Yes</td></tr>
            <tr><td>Conversation memory</td><td>Remembers last 10 messages</td><td>Remembers last 16 messages</td></tr>
          </tbody>
        </table>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-arrow-left-right"></i>
            </div>
            <div className="guide-feature-block-title">How to Switch Modes</div>
          </div>
          <p className="guide-feature-block-desc">
            Look at the <strong>bottom of the chat screen</strong>, near the text input box.
            You'll see a small mode selector button showing either "Simple" or "Smart".
            Simply click it to toggle between the two modes. The change takes effect
            immediately \u2014 your very next message will use the new mode. You can switch
            as many times as you want throughout the day.
          </p>
          <div className="guide-tip">
            <strong>Tip:</strong> A good habit is to stay in Simple Mode for everyday chatting
            and switch to Smart Mode only when you need email, calendar, research, or file
            analysis. Then switch back to Simple when you're done!
          </div>
        </div>
      </>
    ),
  },

  /* ─── Chapter 3: Tasks & Productivity ─── */
  {
    id: "productivity",
    title: "Tasks & Productivity",
    icon: "bi-check2-square",
    intro:
      "This is where Zoomey really shines as your personal assistant. You can manage all your tasks, notes, lists, reminders, groceries, and scheduled automations \u2014 all just by chatting naturally. Everything you create shows up in the left sidebar so you can always see it at a glance. No need to open separate apps for to-do lists, notes, or reminders.",
    content: (
      <>
        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-list-task"></i>
            </div>
            <div className="guide-feature-block-title">Tasks (Your To-Do List)</div>
          </div>
          <p className="guide-feature-block-desc">
            Tell Zoomey what you need to do and it will create a task for you instantly.
            Each task can have a <strong>title</strong> (what you need to do), a
            <strong> description</strong> (extra details), a <strong>due date</strong> (when
            it's due), a <strong>priority</strong> (low, medium, or high), and a
            <strong> category</strong> (like Work, Personal, Shopping, etc.).
          </p>
          <p className="guide-feature-block-desc">
            All your tasks appear in the <strong>left sidebar</strong> under the Tasks tab,
            organized neatly by category. You can click on any task there to see its details.
            To manage tasks, just chat with Zoomey \u2014 you can mark tasks as done, change
            their priority, update the due date, move them to a different category, or delete
            them entirely. Zoomey also understands natural dates like "tomorrow", "next Monday",
            or "end of this week".
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Add task: Finish project report by Friday, high priority"</span>
            <span className="guide-try-chip">"Show all my tasks"</span>
            <span className="guide-try-chip">"What tasks are due today?"</span>
            <span className="guide-try-chip">"Mark the report task as completed"</span>
            <span className="guide-try-chip">"Delete the grocery task"</span>
            <span className="guide-try-chip">"Move 'Call dentist' task to Personal category"</span>
          </div>
          <div className="guide-tip">
            <strong>Tip:</strong> You can create a task with all details in one sentence:
            "Add high priority task under Work: Submit quarterly report by next Friday with
            description 'Include sales figures and charts'."
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-journal-text"></i>
            </div>
            <div className="guide-feature-block-title">Notes (Save Anything)</div>
          </div>
          <p className="guide-feature-block-desc">
            Need to save some information? Just tell Zoomey to create a note. Notes support
            rich formatted text \u2014 bold, italic, headings, bullet points, and more. They
            are organized by categories in your left sidebar under the Notes tab. Great for
            meeting notes, recipes, ideas, project outlines, travel plans, or anything you
            want to save and come back to later.
          </p>
          <p className="guide-feature-block-desc">
            When you click on a note in the sidebar, it opens in a full text editor where you
            can read and edit it directly. You can also ask Zoomey to update a note for you
            through chat \u2014 just tell it what to add or change.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Create a note: Meeting notes from today \u2014 discussed Q2 targets..."</span>
            <span className="guide-try-chip">"Show my notes"</span>
            <span className="guide-try-chip">"Update my Work note with: Added new deadline info"</span>
            <span className="guide-try-chip">"Delete the old project notes"</span>
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-sticky"></i>
            </div>
            <div className="guide-feature-block-title">Scratchpad (Your Quick Sticky Note)</div>
          </div>
          <p className="guide-feature-block-desc">
            See the <strong>"Scratchpad"</strong> button in the top navigation bar? Click it
            to open a quick notepad that floats over your chat screen. It's like having a
            sticky note on your desk \u2014 perfect for jotting down quick thoughts during a
            phone call, copying some text temporarily, drafting a message before sending it,
            or keeping a quick reference visible while you chat.
          </p>
          <p className="guide-feature-block-desc">
            Whatever you type in the Scratchpad is <strong>saved automatically</strong> \u2014
            it will be there exactly as you left it the next time you open Zoomey. You can
            also tell Zoomey to read your Scratchpad content or save it as a proper note.
          </p>
          <div className="guide-tip">
            <strong>Tip:</strong> During a meeting, jot down key points in the Scratchpad.
            After the meeting, tell Zoomey: "Save my scratchpad content as a note under
            Work category called 'Team Meeting Feb 6'."
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-card-checklist"></i>
            </div>
            <div className="guide-feature-block-title">Lists (Checklists for Anything)</div>
          </div>
          <p className="guide-feature-block-desc">
            Create custom lists for literally anything \u2014 packing lists for trips, reading
            lists for books, project checklists, bucket lists, movie watchlists, bookmarks,
            and more. Each list has items you can add, check off as completed, or remove.
            Lists appear in your sidebar under the Lists tab, organized by category.
          </p>
          <p className="guide-feature-block-desc">
            The difference between Tasks and Lists: Tasks are individual things with due dates
            and priorities (like a to-do item). Lists are collections of related items grouped
            together (like a shopping list or a travel packing list). Use whichever fits your
            situation best!
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Create a packing list for my Goa trip"</span>
            <span className="guide-try-chip">"Add sunscreen, charger, and swimwear to packing list"</span>
            <span className="guide-try-chip">"Show all my lists"</span>
            <span className="guide-try-chip">"Check off sunscreen from the packing list"</span>
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-bell"></i>
            </div>
            <div className="guide-feature-block-title">Reminders (Never Forget Anything)</div>
          </div>
          <p className="guide-feature-block-desc">
            Tell Zoomey when to remind you about something, and it will send you an in-app
            notification at exactly that time. You can set <strong>one-time reminders</strong>
            ("remind me tomorrow at 10am to call the doctor") or <strong>recurring
            reminders</strong> ("remind me every Monday at 9am to submit the weekly report").
          </p>
          <p className="guide-feature-block-desc">
            Zoomey understands natural language dates and times, so you can say things like
            "in 2 hours", "next Friday at 3pm", "every day at 8am", "every weekday morning",
            or even "on the 1st of every month". When a reminder fires, you'll see a
            notification popup, and you can view it anytime from the notifications panel.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Remind me to take medicine at 8am every day"</span>
            <span className="guide-try-chip">"Remind me about the dentist tomorrow at 3pm"</span>
            <span className="guide-try-chip">"Show my reminders"</span>
            <span className="guide-try-chip">"Cancel the medicine reminder"</span>
          </div>
          <div className="guide-tip">
            <strong>Tip:</strong> You can manage all your reminders from the top-right user
            menu (click your name) {">"} "Reminders". This shows a clean list of all active
            and past reminders.
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-cart3"></i>
            </div>
            <div className="guide-feature-block-title">Grocery Lists (Smart Shopping)</div>
          </div>
          <p className="guide-feature-block-desc">
            Zoomey has a dedicated grocery management feature that's separate from regular
            lists. Add items with quantities (like "2 kg rice" or "1 liter milk"), check
            them off while you're at the store, and keep your shopping organized. It's like
            having a smart shopping list that you manage just by talking.
          </p>
          <p className="guide-feature-block-desc">
            You can keep adding items throughout the week as you think of them, and when it's
            time to shop, just ask "show my grocery list" to see everything in one place.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Add 2 kg rice, 1 liter milk, and 6 eggs to grocery"</span>
            <span className="guide-try-chip">"Show my grocery list"</span>
            <span className="guide-try-chip">"Remove rice from grocery"</span>
            <span className="guide-try-chip">"Mark milk as bought"</span>
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-clock-history"></i>
            </div>
            <div className="guide-feature-block-title">Scheduled Tasks (Auto-Pilot Actions)</div>
          </div>
          <p className="guide-feature-block-desc">
            Want Zoomey to do something automatically on a schedule without you asking every
            time? Set up a scheduled task! It's like setting a recurring alarm, but instead
            of just ringing, Zoomey actually performs a task for you and sends you the result.
          </p>
          <p className="guide-feature-block-desc">
            For example, you could schedule a daily summary of your tasks every morning at
            9am. Zoomey will automatically check your tasks and send you a notification with
            the summary. Or schedule it to check the weather every Monday morning. The
            possibilities are endless!
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Schedule daily task at 9am: Show my tasks for today"</span>
            <span className="guide-try-chip">"Show my scheduled tasks"</span>
            <span className="guide-try-chip">"Cancel the daily task summary schedule"</span>
          </div>
          <div className="guide-tip">
            <strong>Tip:</strong> View and manage all scheduled tasks from the top-right user
            menu (click your name) {">"} "Scheduled Tasks".
          </div>
        </div>
      </>
    ),
  },

  /* ─── Chapter 4: Search ─── */
  {
    id: "ai-features",
    title: "Search, Memory & Contacts",
    icon: "bi-stars",
    intro:
      "Beyond managing your tasks and notes, Zoomey is also your personal researcher, memory keeper, and contact book. It can search the internet for live information, remember important facts about you and your life, and keep track of all your contacts' details.",
    content: (
      <>
        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-search"></i>
            </div>
            <div className="guide-feature-block-title">Web Search (Live Internet Search)</div>
          </div>
          <p className="guide-feature-block-desc">
            Ask Zoomey anything about the world and it can search the internet <strong>in
            real-time</strong> to find the latest information. Unlike a basic Google search
            that gives you a list of links, Zoomey actually <strong>reads the search results
            for you</strong> and gives you a clear, summarized answer along with sources so
            you can verify. It saves you the trouble of clicking through multiple websites.
          </p>
          <p className="guide-feature-block-desc">
            Great for checking news, comparing prices, getting facts, finding recipes,
            looking up travel info, or answering any question you'd normally Google. The
            information is live and up-to-date because Zoomey searches the web at that moment.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"What's the latest news about cricket?"</span>
            <span className="guide-try-chip">"Search for best budget phones under 15000"</span>
            <span className="guide-try-chip">"What's the weather in Delhi today?"</span>
            <span className="guide-try-chip">"Who won the Oscar for best picture this year?"</span>
          </div>
          <div className="guide-tip">
            <strong>Tip:</strong> Be specific in your search to get better results. Instead of
            "news", try "latest tech news from this week" or "IPL 2026 latest match results".
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-brain"></i>
            </div>
            <div className="guide-feature-block-title">Memory (Zoomey Remembers You)</div>
          </div>
          <p className="guide-feature-block-desc">
            This is one of the coolest features! You can tell Zoomey to <strong>remember
            </strong> anything about you \u2014 your preferences, important dates, personal
            facts, dietary restrictions, favorite things, or anything else. Zoomey stores
            these facts <strong>permanently</strong> and uses them automatically in future
            conversations to give you more personalized help.
          </p>
          <p className="guide-feature-block-desc">
            For example, if you say "remember my favorite food is biryani", next time you
            ask for restaurant suggestions, Zoomey will keep that in mind! If you say
            "remember I'm allergic to peanuts", it will factor that in when suggesting
            recipes. You can ask Zoomey what it remembers about you at any time, or tell
            it to forget something you no longer want stored.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Remember that my wife's birthday is March 15"</span>
            <span className="guide-try-chip">"Remember I'm vegetarian"</span>
            <span className="guide-try-chip">"What do you know about me?"</span>
            <span className="guide-try-chip">"Forget my favorite color"</span>
          </div>
          <div className="guide-note">
            <strong>Good to know:</strong> Memory is different from conversation history.
            Conversation history is temporary (Zoomey remembers last 10-16 messages). But
            things you explicitly ask Zoomey to "remember" are stored permanently until you
            ask it to forget them.
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-person-lines-fill"></i>
            </div>
            <div className="guide-feature-block-title">Contacts (Your Address Book)</div>
          </div>
          <p className="guide-feature-block-desc">
            Save contact information for the people in your life. Store <strong>names</strong>,
            <strong> phone numbers</strong>, <strong>email addresses</strong>, and
            <strong> notes</strong> about each person. Later, you can quickly ask Zoomey for
            someone's number or email instead of searching through your phone contacts.
          </p>
          <p className="guide-feature-block-desc">
            This is also useful in Smart Mode \u2014 when you want to send an email, you can
            reference a saved contact instead of typing the full email address. For example:
            "Send an email to Rahul about tomorrow's meeting" \u2014 Zoomey knows Rahul's
            email because you saved it!
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Save contact: Rahul, phone: 9876543210, email: rahul@gmail.com"</span>
            <span className="guide-try-chip">"What's Rahul's phone number?"</span>
            <span className="guide-try-chip">"Show all my contacts"</span>
            <span className="guide-try-chip">"Update Rahul's email to rahul.sharma@gmail.com"</span>
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-bell-fill"></i>
            </div>
            <div className="guide-feature-block-title">Notifications (Stay in the Loop)</div>
          </div>
          <p className="guide-feature-block-desc">
            Whenever something important happens \u2014 a reminder fires, a scheduled task
            runs, or something needs your attention \u2014 Zoomey sends you an <strong>in-app
            notification</strong>. You'll see a <strong>bell icon</strong> with a red badge
            number in the top navigation bar showing how many unread notifications you have.
          </p>
          <p className="guide-feature-block-desc">
            Click the bell to see all your notifications in the right sidebar. Each
            notification shows what happened and when. You can <strong>reply</strong> to a
            notification \u2014 this opens a special conversation session about that specific
            alert, so you can take action or ask follow-up questions. You can also dismiss
            notifications you've already handled.
          </p>
        </div>
      </>
    ),
  },

  /* ─── Chapter 5: Smart Mode Exclusive Tools ─── */
  {
    id: "smart-tools",
    title: "Smart Mode Features",
    icon: "bi-gem",
    intro:
      "These powerful features are only available when you switch to Smart Mode. They connect Zoomey with your Google account and unlock advanced capabilities like email, calendar, research, and file analysis. To use Google services (email, calendar, docs), you'll need to connect your Google account from your Profile page first (explained at the end of this guide).",
    content: (
      <>
        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-envelope"></i>
            </div>
            <div className="guide-feature-block-title">
              Email (Gmail) <span className="smart-only-tag">Smart Only</span>
            </div>
          </div>
          <p className="guide-feature-block-desc">
            Read, write, search, and manage your Gmail inbox without ever opening Gmail!
            Zoomey can check your latest unread emails, search for specific messages (by
            sender, subject, or date), compose new emails, reply to messages, and more.
            Everything happens through natural conversation \u2014 just say what you want.
          </p>
          <p className="guide-feature-block-desc">
            Before using this feature, you need to connect your Google account once (see
            the "Connecting Google Account" section in the last chapter). After that, Zoomey
            has secure read/write access to your Gmail.
          </p>
          <ol className="guide-steps">
            <li>Make sure you've connected your <strong>Google account</strong> from Profile</li>
            <li>Switch to <strong>Smart Mode</strong> (bottom of chat screen)</li>
            <li>Just ask naturally: "Check my emails" or "Send an email to..."</li>
          </ol>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Check my latest emails"</span>
            <span className="guide-try-chip">"Send email to rahul@gmail.com: Hey, let's meet tomorrow at 3pm"</span>
            <span className="guide-try-chip">"Search emails from my boss this week"</span>
            <span className="guide-try-chip">"Reply to the last email from HR saying I'll attend"</span>
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-calendar-event"></i>
            </div>
            <div className="guide-feature-block-title">
              Google Calendar <span className="smart-only-tag">Smart Only</span>
            </div>
          </div>
          <p className="guide-feature-block-desc">
            See your schedule, create new events, check if you're free, and manage your
            calendar \u2014 all through chat. Zoomey connects to your Google Calendar so you
            can manage your day without switching apps. Ask about today's events, add a
            meeting for next week, or check your availability before planning something.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"What's on my calendar today?"</span>
            <span className="guide-try-chip">"Add a meeting tomorrow at 2pm: Team sync-up"</span>
            <span className="guide-try-chip">"Am I free this Friday afternoon?"</span>
            <span className="guide-try-chip">"Show my schedule for next week"</span>
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-file-earmark-text"></i>
            </div>
            <div className="guide-feature-block-title">
              Google Docs, Drive & Sheets <span className="smart-only-tag">Smart Only</span>
            </div>
          </div>
          <p className="guide-feature-block-desc">
            Access your Google Drive files, read Google Docs, and work with Google Sheets
            \u2014 all through conversation. You can search for files by name, read document
            contents, update spreadsheet data, and more. This is incredibly handy when you
            want to quickly check something in a document without opening Google Drive.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Find my recent documents in Drive"</span>
            <span className="guide-try-chip">"Read the project proposal document"</span>
            <span className="guide-try-chip">"Update row 5 in my budget spreadsheet"</span>
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-binoculars"></i>
            </div>
            <div className="guide-feature-block-title">
              Deep Research <span className="smart-only-tag">Smart Only</span>
            </div>
          </div>
          <p className="guide-feature-block-desc">
            Need a really thorough, in-depth answer? Deep Research goes far beyond a simple
            web search. When you use it, Zoomey searches <strong>multiple websites</strong>,
            reads through all the content carefully, <strong>cross-references
            information</strong> from different sources, and compiles a <strong>detailed,
            well-organized report</strong> for you with citations and sources.
          </p>
          <p className="guide-feature-block-desc">
            It takes a couple of minutes (because it's doing real research work), but you
            get a proper research summary \u2014 like having a personal research assistant
            who reads 10+ articles and gives you the best of everything in one place.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Do deep research on best laptops under 50000 in India"</span>
            <span className="guide-try-chip">"Research the pros and cons of electric cars vs petrol"</span>
            <span className="guide-try-chip">"Research best schools in Bangalore for grade 5"</span>
          </div>
          <div className="guide-tip">
            <strong>Tip:</strong> Use "deep research" in your message to trigger this feature.
            Without those keywords, Zoomey does a regular quick web search instead.
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-paperclip"></i>
            </div>
            <div className="guide-feature-block-title">
              File & Image Analysis <span className="smart-only-tag">Smart Only</span>
            </div>
          </div>
          <p className="guide-feature-block-desc">
            Upload documents (PDFs, Word files), images (photos, screenshots), or other files
            and Zoomey will analyze them for you. It can <strong>summarize a long PDF</strong>,
            <strong> extract key points</strong>, <strong>read text from an image</strong>
            (like a photo of a bill or receipt), <strong>describe what's in a photo</strong>,
            or answer any questions about the file's contents.
          </p>
          <p className="guide-feature-block-desc">
            To upload a file, click the <strong>attachment icon (paperclip)</strong> in the
            chat input area and select your file. Then type your question about it, like
            "Summarize this PDF" or "What does this image show?"
          </p>
          <div className="guide-try-label">Try typing these (after uploading a file)</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Summarize this PDF for me"</span>
            <span className="guide-try-chip">"What does this image show?"</span>
            <span className="guide-try-chip">"Extract the main points from this document"</span>
            <span className="guide-try-chip">"Read the text in this screenshot"</span>
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-bookshelf"></i>
            </div>
            <div className="guide-feature-block-title">
              Second Brain (Your Knowledge Library) <span className="smart-only-tag">Smart Only</span>
            </div>
          </div>
          <p className="guide-feature-block-desc">
            Your personal knowledge base! The Second Brain lets you organize information
            into 4 categories: <strong>Projects</strong> (things you're actively working on),
            <strong> Areas</strong> (ongoing responsibilities like health, finances, career),
            <strong> Resources</strong> (reference material you might need later), and
            <strong> Archives</strong> (completed or inactive items).
          </p>
          <p className="guide-feature-block-desc">
            Think of it as a super-organized digital filing cabinet for all your knowledge.
            You can save research, articles, ideas, or any information and retrieve it later
            by simply asking Zoomey. It's different from Notes because Second Brain is
            specifically designed for organizing knowledge by project/area, while Notes are
            for simpler, standalone content.
          </p>
          <div className="guide-try-label">Try typing these</div>
          <div className="guide-try-examples">
            <span className="guide-try-chip">"Save to second brain under Projects: Website redesign notes..."</span>
            <span className="guide-try-chip">"Search my second brain for marketing ideas"</span>
            <span className="guide-try-chip">"What's in my Resources section?"</span>
          </div>
        </div>
      </>
    ),
  },

  /* ─── Chapter 6: Navigating the UI ─── */
  {
    id: "ui-guide",
    title: "Navigating the Screen",
    icon: "bi-layout-wtf",
    intro:
      "Zoomey's screen is divided into three main areas: a left sidebar, the center chat area, and a right sidebar. Understanding this layout will help you find everything quickly and use Zoomey like a pro. Here's a complete walkthrough of every part of the screen.",
    content: (
      <>
        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-layout-sidebar-inset"></i>
            </div>
            <div className="guide-feature-block-title">Left Sidebar (Your Organizer)</div>
          </div>
          <p className="guide-feature-block-desc">
            The left panel is your command center for everything you've created. It has tabs
            at the top for different sections:
          </p>
          <ul className="guide-steps">
            <li><strong>Topics</strong> \u2014 Switch between different conversation topics (or stay in Main Topic)</li>
            <li><strong>Tasks</strong> \u2014 See all your tasks organized neatly by categories. Click any task to view details</li>
            <li><strong>Notes</strong> \u2014 Browse all your saved notes. Click one to open it in the built-in text editor</li>
            <li><strong>Lists</strong> \u2014 View all your custom lists with their items and checkboxes</li>
          </ul>
          <p className="guide-feature-block-desc">
            You can <strong>collapse (hide)</strong> the sidebar by clicking the toggle
            button at the top-left to get more space for chatting. Click it again to bring
            it back. On smaller screens, the sidebar is hidden by default.
          </p>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-chat-left-text"></i>
            </div>
            <div className="guide-feature-block-title">Center (The Chat Area)</div>
          </div>
          <p className="guide-feature-block-desc">
            This is where all the magic happens \u2014 your conversation with Zoomey appears
            here as message bubbles. Your messages appear on one side, Zoomey's responses on
            the other. At the very bottom, you'll see the text input box where you type. Around
            the input box, you'll find:
          </p>
          <ul className="guide-steps">
            <li><strong>Microphone icon</strong> \u2014 Click to start voice input (speak instead of typing)</li>
            <li><strong>Attachment icon (paperclip)</strong> \u2014 Upload files for analysis (Smart Mode)</li>
            <li><strong>Mode selector</strong> \u2014 Toggle between Simple and Smart mode</li>
            <li><strong>Send button</strong> \u2014 Click to send your message (or just press Enter)</li>
          </ul>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-layout-sidebar-inset-reverse"></i>
            </div>
            <div className="guide-feature-block-title">Right Sidebar (Notifications & Queue)</div>
          </div>
          <p className="guide-feature-block-desc">
            The right side shows your <strong>notifications</strong> (reminder alerts, task
            updates, scheduled task results), <strong>queued requests</strong> (if you
            sent multiple requests), and <strong>meeting information</strong>. Open it by
            clicking the notification bell or the panel icons on the right edge. You'll see
            real-time alerts for everything happening in your Zoomey world.
          </p>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-palette"></i>
            </div>
            <div className="guide-feature-block-title">Top Navigation Bar (Quick Actions)</div>
          </div>
          <p className="guide-feature-block-desc">
            The bar at the very top of the screen shows your current topic name and has
            these useful buttons from left to right:
          </p>
          <ul className="guide-steps">
            <li><strong>Sidebar toggle</strong> \u2014 Show/hide the left sidebar</li>
            <li><strong>Topic name</strong> \u2014 Shows which topic you're currently in</li>
            <li><strong>Sun/Moon icon</strong> \u2014 Switch between dark and light theme</li>
            <li><strong>Scratchpad</strong> \u2014 Open your floating quick notepad</li>
            <li><strong>? (Question mark)</strong> \u2014 Opens this guide you're reading right now!</li>
            <li><strong>Clear Chat</strong> \u2014 Clears your current conversation screen (only visible on chat page)</li>
            <li><strong>Your Name (dropdown menu)</strong> \u2014 Click to access: Profile, Reminders, Scheduled Tasks, Main Topic, and Logout</li>
          </ul>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-circle-half"></i>
            </div>
            <div className="guide-feature-block-title">Dark & Light Theme</div>
          </div>
          <p className="guide-feature-block-desc">
            Click the <strong>sun icon</strong> (when in light mode) or <strong>moon icon
            </strong> (when in dark mode) in the top bar to switch between themes. A smooth
            loading animation will play while the theme changes. Your preference is
            <strong> saved automatically</strong>, so next time you open Zoomey, it will use
            the same theme you chose last time.
          </p>
          <div className="guide-tip">
            <strong>Tip:</strong> Dark mode is easier on the eyes at night and can save
            battery on phones and laptops with OLED/AMOLED screens.
          </div>
        </div>

        <div className="guide-feature-block">
          <div className="guide-feature-block-header">
            <div className="guide-feature-block-icon">
              <i className="bi bi-link-45deg"></i>
            </div>
            <div className="guide-feature-block-title">Connecting Your Google Account</div>
          </div>
          <p className="guide-feature-block-desc">
            To use email, calendar, and Google services in Smart Mode, you need to connect
            your Google account one time. Here's exactly how to do it:
          </p>
          <ol className="guide-steps">
            <li>Click your <strong>name</strong> in the top-right corner to open the dropdown menu</li>
            <li>Select <strong>"Profile"</strong></li>
            <li>Scroll down to the <strong>"Integrations"</strong> section</li>
            <li>Click the <strong>"Connect"</strong> button next to Google</li>
            <li>A Google login window will appear \u2014 sign in with your Google account and click "Allow"</li>
          </ol>
          <p className="guide-feature-block-desc">
            That's it! You only need to do this <strong>once</strong>. Zoomey uses a
            secure connection method called OAuth, which means it <strong>never sees or stores
            your Google password</strong>. You can disconnect your Google account anytime
            from the same Profile {">"} Integrations page if you change your mind.
          </p>
          <div className="guide-note">
            <strong>Good to know:</strong> Without connecting Google, you can still use all
            Simple Mode features and many Smart Mode features (deep research, file analysis,
            second brain). Only email, calendar, and Google Docs/Drive/Sheets require the
            Google connection.
          </div>
        </div>
      </>
    ),
  },
];

/* =============================================
   COMPONENT
   ============================================= */
function UserGuideModal({ show, onHide }) {
  const [activeChapter, setActiveChapter] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeChapter]);

  useEffect(() => {
    if (show) {
      setActiveChapter(0);
    }
  }, [show]);

  const chapter = CHAPTERS[activeChapter];

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      dialogClassName="user-guide-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-book" style={{ color: "var(--teal-bg)" }}></i>
          Zoomey User Guide
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="guide-toc">
          {CHAPTERS.map((ch, idx) => (
            <div
              key={ch.id}
              className={`guide-toc-item ${idx === activeChapter ? "active" : ""}`}
              onClick={() => setActiveChapter(idx)}
            >
              <i className={`bi ${ch.icon}`}></i>
              <span>{ch.title}</span>
            </div>
          ))}
        </div>

        <div className="guide-content" ref={contentRef}>
          <div className="guide-chapter-header">
            <div className="guide-chapter-num">
              Chapter {activeChapter + 1} of {CHAPTERS.length}
            </div>
            <div className="guide-chapter-title">
              <i className={`bi ${chapter.icon}`}></i>
              {chapter.title}
            </div>
            <p className="guide-chapter-intro">{chapter.intro}</p>
          </div>

          {chapter.content}
        </div>
      </Modal.Body>

      <div className="guide-nav-footer">
        <button
          className="guide-nav-btn"
          disabled={activeChapter === 0}
          onClick={() => setActiveChapter((p) => p - 1)}
        >
          <i className="bi bi-chevron-left"></i> Previous
        </button>

        <span className="guide-page-indicator">
          {activeChapter + 1} / {CHAPTERS.length}
        </span>

        {activeChapter < CHAPTERS.length - 1 ? (
          <button
            className="guide-nav-btn"
            onClick={() => setActiveChapter((p) => p + 1)}
          >
            Next <i className="bi bi-chevron-right"></i>
          </button>
        ) : (
          <button className="guide-nav-btn primary" onClick={onHide}>
            Got it! <i className="bi bi-check-lg"></i>
          </button>
        )}
      </div>
    </Modal>
  );
}

export default UserGuideModal;
